import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { toastApiError } from "@/lib/toast-error";
import {
  type AddressPreferencePut,
  type FeedInfiniteData,
  NOTIFICATION_PREFS_KEY,
  NOTIFICATIONS_FEED_KEY,
  NOTIFICATIONS_UNREAD_KEY,
  notificationsApi,
  type PreferencePut,
  type PreferencesResponse,
  REMINDER_SETTINGS_KEY,
  type ReminderSettings,
} from "./notification-data";

/**
 * The unread badge count. Seeded by this query on mount, then kept live by the
 * websocket handler writing NOTIFICATIONS_UNREAD_KEY directly (use-websocket.tsx)
 * and the reconnect invalidation. Those cover every change, so the app-default
 * focus refetch (an unbounded COUNT(*) server-side, fired from the global
 * header on every tab refocus) is pure duplication — disabled here.
 */
export function useUnreadCount(enabled: boolean) {
  const { data } = useQuery({
    queryKey: NOTIFICATIONS_UNREAD_KEY,
    queryFn: notificationsApi.fetchUnreadCount,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  return data?.unread_count ?? 0;
}

/** Paged feed (newest first). Only fetches once the panel is opened. */
export function useNotificationFeed(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_FEED_KEY,
    queryFn: ({ pageParam }) => notificationsApi.fetchFeedPage(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.notifications.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled,
  });
}

/**
 * Mark one notification (by id) or all unread as read. Optimistically flips
 * read_at in the feed cache and drops the badge; on settle the badge refetches
 * so a `notification:new` that raced the POST isn't clobbered by the response's
 * (older) count.
 */
export function useMarkRead() {
  return useMutation({
    mutationFn: (notificationId?: string) => notificationsApi.postRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel in-flight fetches: a GET snapshotted before the POST would
      // otherwise resolve afterwards and resurrect the rows as unread.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_FEED_KEY }),
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY }),
      ]);
      const readAt = new Date().toISOString();
      queryClient.setQueryData<FeedInfiniteData>(NOTIFICATIONS_FEED_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            notifications: page.notifications.map((n) =>
              (notificationId ? n.notification_id === notificationId : true) && !n.read_at
                ? { ...n, read_at: readAt }
                : n,
            ),
          })),
        };
      });
      queryClient.setQueryData<{ unread_count: number }>(NOTIFICATIONS_UNREAD_KEY, (old) =>
        notificationId
          ? { unread_count: Math.max(0, (old?.unread_count ?? 1) - 1) }
          : { unread_count: 0 },
      );
    },
    onSettled: () => {
      // Refetch the badge truth (cheap, and mark-read is a rare user click);
      // on error also pull the feed back since the optimistic flips are wrong.
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY });
    },
  });
}

/** The §5.6 two-lane preferences, grouped server-side by comms group. */
export function usePreferences() {
  return useQuery({
    queryKey: NOTIFICATION_PREFS_KEY,
    queryFn: notificationsApi.fetchPreferences,
  });
}

/**
 * Flip one lane of one category. Optimistic: the switch moves immediately;
 * an error rolls back to the server truth.
 */
export function useUpdatePreference() {
  return useMutation({
    mutationFn: (body: PreferencePut) => notificationsApi.putPreference(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFS_KEY });
      queryClient.setQueryData<PreferencesResponse>(NOTIFICATION_PREFS_KEY, (old) =>
        old
          ? {
              ...old,
              preferences: old.preferences.map((p) =>
                p.category === body.category
                  ? {
                      ...p,
                      is_push_enabled: body.is_push_enabled ?? p.is_push_enabled,
                      is_inbox_enabled: body.is_inbox_enabled ?? p.is_inbox_enabled,
                      is_email_enabled: body.is_email_enabled ?? p.is_email_enabled,
                      // A category-level flip clears the mixed state either way.
                      is_email_partial: body.is_email_enabled == null ? p.is_email_partial : false,
                    }
                  : p,
              ),
            }
          : old,
      );
    },
    // The switch snapping back is the only sign the save failed — say so, or it
    // reads as an unresponsive toggle.
    onError: (e) => {
      toastApiError(e, "Couldn't save that notification setting.");
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY });
    },
    onSuccess: () => toast({ description: "Saved.", variant: "success" }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY });
    },
  });
}

/** Per-address reminder / marketing opt-out (D10). Optimistic like the category lanes. */
export function useUpdateAddressPreference() {
  return useMutation({
    mutationFn: (body: AddressPreferencePut) => notificationsApi.putAddressPreference(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFS_KEY });
      queryClient.setQueryData<PreferencesResponse>(NOTIFICATION_PREFS_KEY, (old) =>
        old
          ? {
              ...old,
              email_addresses: old.email_addresses.map((a) =>
                a.user_email_id === body.user_email_id
                  ? {
                      ...a,
                      reminders_enabled: body.reminders_enabled ?? a.reminders_enabled,
                      marketing_enabled: body.marketing_enabled ?? a.marketing_enabled,
                    }
                  : a,
              ),
            }
          : old,
      );
    },
    onError: (e) => {
      toastApiError(e, "Couldn't save that email setting.");
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY });
    },
    onSuccess: () => toast({ description: "Saved.", variant: "success" }),
  });
}

/** Per-member reminder cadence (lead days + send hour). */
export function useReminderSettings() {
  return useQuery({
    queryKey: REMINDER_SETTINGS_KEY,
    queryFn: notificationsApi.fetchReminderSettings,
  });
}

export function useUpdateReminderSettings() {
  return useMutation({
    mutationFn: (body: ReminderSettings) => notificationsApi.putReminderSettings(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: REMINDER_SETTINGS_KEY });
      queryClient.setQueryData<ReminderSettings>(REMINDER_SETTINGS_KEY, body);
    },
    onError: (e) => {
      toastApiError(e, "Couldn't save your reminder settings.");
      void queryClient.invalidateQueries({ queryKey: REMINDER_SETTINGS_KEY });
    },
    onSuccess: () => {
      // The server dedups/sorts lead_days — pull its canonical shape back.
      void queryClient.invalidateQueries({ queryKey: REMINDER_SETTINGS_KEY });
      toast({ description: "Saved.", variant: "success" });
    },
  });
}
